import { FC, useEffect, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import {
  Button,
  InputNumberWithButtons,
  DialogWithHero,
} from "@jugl-web/ui-kit";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as WarningIcon } from "./assets/warning.svg";

export interface AddAITeamMembersProps {
  isOpen: boolean;
  withWarning?: boolean;
  onClose: () => void;
  currentQuantity?: number;
  minQuantity?: number;
  pricePerMember?: number;
  isLoading?: boolean;
  onBuy?: (newQuantity: number) => void;
}

const DEFAULT_PRICE_PER_MEMBER = 25;

export const AddAITeamMembers: FC<AddAITeamMembersProps> = ({
  isOpen,
  withWarning = false,
  onClose,
  currentQuantity = 0,
  minQuantity = 0,
  pricePerMember = DEFAULT_PRICE_PER_MEMBER,
  isLoading = false,
  onBuy,
}) => {
  const { t } = useTranslations();
  const [members, setMembers] = useState(currentQuantity);

  useEffect(() => {
    if (isOpen) {
      setMembers(currentQuantity);
    }
  }, [isOpen, currentQuantity]);

  const totalPrice = members * pricePerMember;
  const addedSeats = members - currentQuantity;
  const hasChanges = members !== currentQuantity;
  const isReducing = members < currentQuantity;

  return (
    <DialogWithHero
      isOpen={isOpen}
      onClose={onClose}
      upperTitle={t(
        {
          id: "add-ai-team-members-dialog.upper-title",
          // oxlint-disable-next-line no-template-curly-in-string
          defaultMessage: "${price} per member / month",
        },
        { price: pricePerMember }
      )}
      title={
        <span className="font-secondary text-[40px] leading-[1.2] font-semibold text-white">
          {t({
            id: "add-ai-team-members-dialog.title",
            defaultMessage: "Extra Team Members",
          })}
        </span>
      }
      description=""
      headerChildren={
        <InputNumberWithButtons
          value={members}
          onChange={setMembers}
          min={minQuantity}
          formatValue={(v) => v.toString()}
        />
      }
    >
      <div className="mx-auto flex max-w-[660px] flex-col gap-6 pt-6">
        {withWarning && <WarningSection />}
        {currentQuantity > 0 && (
          <div className="flex items-center justify-between font-secondary text-base text-dark">
            <span>
              {t(
                {
                  id: "add-ai-team-members-dialog.active-seats",
                  defaultMessage: "{count} Active extra seats",
                },
                { count: currentQuantity }
              )}
            </span>
            <span className="font-medium">
              ${currentQuantity * pricePerMember}
            </span>
          </div>
        )}
        {addedSeats > 0 && (
          <ExtrasSeatsSection
            extraSeats={addedSeats}
            pricePerMember={pricePerMember}
          />
        )}
        <div className="h-px bg-dark-100" />
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="font-secondary font-medium">
              {t({
                id: "add-ai-team-members-dialog.total-per-month",
                defaultMessage: "Total per month",
              })}
            </span>
            {currentQuantity > 0 && hasChanges && (
              <span className="font-secondary text-sm text-dark-700">
                {t({
                  id: "add-ai-team-members-dialog.prorated-amount",
                  defaultMessage: "You will be charged a prorated amount",
                })}
              </span>
            )}
          </div>

          <span className="font-secondary text-lg font-semibold text-dark">
            ${totalPrice}
          </span>
        </div>
        <div className="mt-6 flex justify-center">
          <Button
            variant="primary"
            size="lg"
            className="min-w-[300px]"
            isLoading={isLoading}
            isDisabled={!hasChanges}
            iconEnd={<ArrowRightIcon />}
            onClick={() => onBuy?.(members)}
          >
            {isReducing
              ? t({
                  id: "add-ai-team-members-dialog.save",
                  defaultMessage: "Save",
                })
              : t({
                  id: "add-ai-team-members-dialog.buy",
                  defaultMessage: "Buy",
                })}
          </Button>
        </div>
      </div>
    </DialogWithHero>
  );
};

const WarningSection: FC = () => {
  const { t } = useTranslations();
  return (
    <div className="flex items-center gap-3 rounded-xl bg-tertiary/5 p-3">
      <WarningIcon />
      <span className="font-secondary text-sm text-dark">
        {t({
          id: "add-ai-team-members-dialog.warning",
          defaultMessage:
            "You are about to buy extra team members. This will increase your monthly cost.",
        })}
      </span>
    </div>
  );
};

const ExtrasSeatsSection: FC<{
  extraSeats: number;
  pricePerMember: number;
}> = ({ extraSeats, pricePerMember }) => {
  const { t } = useTranslations();
  return (
    <div className="flex items-center justify-between font-secondary text-base text-dark">
      <div className="flex items-center gap-2">
        <div className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-secondary/20 px-1 font-secondary text-[11px] font-semibold text-dark">
          +{extraSeats}
        </div>
        <span>
          {t({
            id: "add-ai-team-members-dialog.extra-seat",
            defaultMessage: "Extra seat",
          })}
        </span>
      </div>
      <span className="font-medium">${extraSeats * pricePerMember}</span>
    </div>
  );
};
